import React, { useState } from "react";
import { NavLink as NavLinkRRD, Link } from "react-router-dom";
import { PropTypes } from "prop-types";
import {
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
  DropdownToggle,
  Form,
  Input,
  InputGroupAddon,
  InputGroupText,
  InputGroup,
  Media,
  Navbar,
  NavItem,
  NavLink,
  Nav,
  Container,
} from "reactstrap";
import { PiBriefcaseBold } from "react-icons/pi";
import logo from "../../assets/img/Logo-png.svg";
import { FaChevronRight } from "react-icons/fa";

const PRIMARY_MENU_ROUTES = [
  "Dashboard",
  "User Management",
  "Verification Management",
  "Booking Management",
  "Reviews & Ratings",
  "Help & Support",
  "Report Management",
  // "Safety & Verification",
  // "Payments & Revenue",
  // "Content Management",
  // "Analytics",
  // "Settings",
  "Membership Management",
  "Tour Package Management",
  "Popular Destinations",
];

const MANAGEMENT_ROUTES = [
  "Role Management",
  "Privacy Policy",
  "Terms & Conditions",
  "Contact Us",
  "Tour Categories Management",
  "FAQ Management",
];

const Sidebar = ({ isPinned, isHovered, routes }) => {
  const [collapseState, setCollapseState] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const isSidebarClosed = !isPinned && !isHovered;

  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
  };

  const toggleManagement = (e) => {
    e.preventDefault();

    setCollapseState((prev) =>
      prev === "management" ? "" : "management"
    );
  };

  /**
   * Render icon for a route.
   */
  const renderIcon = (route) => {
    const { icon, name } = route;

    if (!icon) {
      return null;
    }

    const iconStyle = {
      width: "18px",
      height: "18px",
      marginRight: isSidebarClosed ? "0px" : "10px",
    };

    const colorClass = "button-secondary-color sidebar-menu-icon";

    // String icon
    if (typeof icon === "string") {
      return (
        <i
          className={`${icon} ${colorClass}`}
          title={name}
          style={{
            fontSize: "18px",
            ...iconStyle,
          }}
        />
      );
    }

    // Already-created React element
    if (React.isValidElement(icon)) {
      return React.cloneElement(icon, {
        title: name,
        className: `${icon.props?.className || ""} ${colorClass}`.trim(),
        style: {
          ...iconStyle,
          ...icon.props?.style,
        },
      });
    }

    if (typeof icon === "function" || typeof icon === "object") {
      const Icon = icon;

      return (
        <Icon
          title={name}
          className={colorClass}
          style={iconStyle}
        />
      );
    }

    return null;
  };

  const createLinks = (routeList = []) => {
    return routeList
      .filter(
        (route) =>
          route?.name &&
          route?.layout === "/admin"
      )
      .map((route, index) => (
        <NavItem key={`${route.name}-${index}`}>
          <NavLink
            to={`${route.layout}${route.path}`}
            tag={NavLinkRRD}
            title={isSidebarClosed ? route.name : ""}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: isSidebarClosed
                ? "center"
                : "flex-start",
              padding: "10px 15px",
            }}
          >
            {renderIcon(route)}

            {!isSidebarClosed && (
              <span>{route.name}</span>
            )}
          </NavLink>
        </NavItem>
      ));
  };

  const checkIsSuperAdmin = () => {
    try {
      const userData = JSON.parse(localStorage.getItem("userData") || "{}");
      if (userData?.isSuperAdmin === true) return true;
      if (
        userData?.type === "superadmin" ||
        userData?.type === "super_admin" ||
        userData?.roleName?.toLowerCase()?.includes("super")
      ) {
        return true;
      }
      const userRole = (
        localStorage.getItem("userRole") ||
        userData?.roleName ||
        userData?.type ||
        ""
      )
        .toLowerCase()
        .trim();
      if (
        userRole === "super admin" ||
        userRole === "superadmin" ||
        userRole === "super_admin" ||
        userRole.includes("super")
      ) {
        return true;
      }
      return false;
    } catch (e) {
      return false;
    }
  };

  const renderManagement = () => {
    const isSuperAdmin = checkIsSuperAdmin();
    let userPermissions = [];
    try {
      userPermissions = JSON.parse(localStorage.getItem("userPermissions") || "[]");
    } catch (e) {
      userPermissions = [];
    }

    const managementRoutes = routes.filter(
      (route) =>
        route?.name &&
        route?.layout === "/admin" &&
        MANAGEMENT_ROUTES.includes(route.name) &&
        (isSuperAdmin || userPermissions.includes(route.name))
    );

    if (!managementRoutes.length) {
      return null;
    }

    return (
      <>
        <NavItem>
          <NavLink
            href="#management"
            onClick={toggleManagement}
            className={`nav-link ${collapseState === "management" ? "active" : ""
              }`}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: isSidebarClosed
                ? "center"
                : "flex-start",
              padding: "10px 15px",
            }}
          >
            <PiBriefcaseBold size={20} className="button-secondary-color sidebar-menu-icon mr-2" style={{ marginRight: isSidebarClosed ? '0px' : '10px' }} />

            {!isSidebarClosed && (
              <span className="d-flex align-items-center justify-content-between flex-grow-1">
                Management <FaChevronRight size={11} className="button-secondary-color" style={{ marginLeft: "8px" }} />
              </span>
            )}
          </NavLink>
        </NavItem>

        {collapseState === "management" &&
          managementRoutes.map((route, index) => (
            <NavItem key={`${route.name}-${index}`}>
              <NavLink
                to={`${route.layout}${route.path}`}
                tag={NavLinkRRD}
                title={
                  isSidebarClosed
                    ? route.name
                    : ""
                }
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: isSidebarClosed
                    ? "center"
                    : "flex-start",
                  padding: "10px 15px",
                }}
              >
                {renderIcon(route)}

                {!isSidebarClosed && (
                  <span>{route.name}</span>
                )}
              </NavLink>
            </NavItem>
          ))}
      </>
    );
  };

  /**
   * Sidebar navigation.
   */
  const renderNavigation = (mobile = false) => {
    const isSuperAdmin = checkIsSuperAdmin();
    let userPermissions = [];
    try {
      userPermissions = JSON.parse(localStorage.getItem("userPermissions") || "[]");
    } catch (e) {
      userPermissions = [];
    }

    const normalRoutes = routes.filter(
      (route) =>
        route?.name &&
        route?.layout === "/admin" &&
        PRIMARY_MENU_ROUTES.includes(route.name) &&
        (isSuperAdmin || userPermissions.includes(route.name))
    );

    return (
      <Nav
        navbar
        className={
          mobile
            ? "h-87vh overflow-auto"
            : ""
        }
      >
        {createLinks(normalRoutes)}
        {renderManagement()}
      </Nav>
    );
  };

  return (
    <Navbar
      className={`navbar-vertical fixed-left navbar-light background-blue-light px-2 ${isPinned ? "sidebar-pinned" : ""
        } ${isHovered && !isPinned
          ? "sidebar-hovered"
          : ""
        }`}
      expand="md"
      id="sidenav-main"
    >
      <Container fluid className="justify-content-start gap-40">
        {/* Header */}
        <div className="w-100 d-flex justify-content-between align-items-center gap-2">
          {/* Mobile toggle */}
          <button
            type="button"
            onClick={toggleSidebar}
            className="navbar-toggler"
          >
            <span className="navbar-toggler-icon" />
          </button>

          {/* Logo */}
          <div
            className={`w-100 d-flex align-items-center brand-container ${isPinned || isHovered
              ? "justify-content-center"
              : "justify-content-center"
              }`}
          >
            <img
              src={logo}
              alt="Area Trek Logo"
              title="Area Trek Dashboard"
              onClick={() => {
                window.location.href = "/admin/index";
              }}
              className={
                isPinned || isHovered
                  ? "w-110 object-fit-cover cursor-pointer"
                  : "w-34 h-34 cursor-pointer"
              }
            />

          </div>

          {/* Mobile user menu */}
          <div className="d-flex align-items-center d-md-none">
            {/* <UncontrolledDropdown nav>
              <DropdownToggle
                nav
                className="nav-link-icon"
              >
                <i className="ni ni-bell-55" />
              </DropdownToggle>

              <DropdownMenu
                aria-labelledby="navbar-default_dropdown_1"
                className="dropdown-menu-arrow"
                right
              >
                <DropdownItem>
                  Action
                </DropdownItem>

                <DropdownItem>
                  Another action
                </DropdownItem>

                <DropdownItem divider />

                <DropdownItem>
                  Something else here
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown> */}

            <UncontrolledDropdown nav>
              <DropdownToggle nav>
                <Media className="align-items-center">
                  <span className="avatar avatar-sm rounded-circle">
                    <img alt="Area Trek Logo" src={logo} />

                  </span>
                </Media>
              </DropdownToggle>

              <DropdownMenu
                className="dropdown-menu-arrow"
                right
              >
                <DropdownItem
                  className="noti-title"
                  header
                  tag="div"
                >
                  <h6 className="text-overflow m-0">
                    Welcome!
                  </h6>
                </DropdownItem>

                <DropdownItem
                  to="/admin/profile"
                  tag={Link}
                >
                  <i className="ni ni-single-02" />
                  <span>My profile</span>
                </DropdownItem>

                <DropdownItem divider />

                <DropdownItem
                  href="#logout"
                  onClick={(e) =>
                    e.preventDefault()
                  }
                >
                  <i className="ni ni-user-run" />
                  <span>Logout</span>
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
          </div>
        </div>

        {/* Desktop Sidebar */}
        <div className="d-none d-md-block">
          {/* Search */}
          <Form className="mt-4 mb-3 d-none">
            <InputGroup className="input-group-rounded input-group-merge">
              <Input
                aria-label="Search"
                className="form-control-rounded form-control-prepended"
                placeholder="Search"
                type="search"
              />

              <InputGroupAddon addonType="prepend">
                <InputGroupText>
                  <span className="fa fa-search" />
                </InputGroupText>
              </InputGroupAddon>
            </InputGroup>
          </Form>

          {/* Navigation */}
          {renderNavigation()}
        </div>

        {/* Mobile Overlay Sidebar */}
        <div
          className={`sidebar-overlay ${sidebarOpen ? "show" : ""
            }`}
          onClick={toggleSidebar}
        >
          <div
            className={`sidebar-panel ${sidebarOpen ? "open" : ""
              }`}
            onClick={(e) =>
              e.stopPropagation()
            }
          >
            {/* Search */}
            <Form className="mt-4 mb-3">
              <InputGroup className="input-group-rounded input-group-merge">
                <Input
                  aria-label="Search"
                  className="form-control-rounded form-control-prepended"
                  placeholder="Search"
                  type="search"
                />

                <InputGroupAddon addonType="prepend">
                  <InputGroupText>
                    <span className="fa fa-search" />
                  </InputGroupText>
                </InputGroupAddon>
              </InputGroup>
            </Form>

            {/* Navigation */}
            {renderNavigation(true)}
          </div>
        </div>
      </Container>
    </Navbar>
  );
};

Sidebar.defaultProps = {
  routes: [],
  isPinned: false,
  isHovered: false,
};

Sidebar.propTypes = {
  routes: PropTypes.arrayOf(
    PropTypes.object
  ),

  isPinned: PropTypes.bool,

  isHovered: PropTypes.bool,
};

export default Sidebar;
